import { put, call, select, take, race, all, delay } from 'redux-saga/effects';
import { roleIsAtLeast, userRoles } from '@oup/shared-node-browser/user';

import {
  EVENT_POLLING_START,
  EVENT_POLLING_STOP,
  pollStartAction,
  pollStopByResourceAction,
  getDataFailureAction
} from '../../reducers/polling.reducer';

import { pollConfig, learnerPollingEvents, teacherPollingEvents } from '../../../globals/pollingConfig';

const moduleName = '[polling Saga]';

function getPollEventConfig(pollFor, currentUserRole) {
  // the user role check was added for backward compatibility as this polling was only for teachers intially

  if (currentUserRole === userRoles.LEARNER) {
    return learnerPollingEvents[pollFor] || null;
  }

  if (roleIsAtLeast(userRoles.TEACHER, currentUserRole)) {
    return teacherPollingEvents[pollFor] || null;
  }

  return null;
}

function* pollEventSaga(pollFor, pollEvent, userRole) {
  const { pollingParams } = pollEvent || {};
  const pollEventConfig = getPollEventConfig(pollFor, userRole);

  console.log(pollEventConfig);

  if (!pollEventConfig) {
    console.log(moduleName, `No polling event configured for: ${pollFor}`);
    yield put(pollStopByResourceAction(pollFor));
    return;
  }

  const {
    retries = pollConfig.retries,
    stopAfterRetries = pollConfig.stopAfterRetries,
    throttleInterval = pollConfig.throttleInterval,
    conditionToFulfil,
    apiFailed
  } = pollEventConfig;

  let retryCount = retries - 1;
  let intervalResetCount = pollConfig.throttleIntervalReset;
  const isArrayInterval = Array.isArray(throttleInterval);
  const intervalLimit = isArrayInterval ? throttleInterval.length : null;

  while (true) {
    try {
      console.log(moduleName, 'Inside pollingSaga try block for polling event: ', pollFor);

      const intervalMs =
        (isArrayInterval ? throttleInterval[retryCount] || throttleInterval[intervalLimit - 1] : throttleInterval) *
        1000;

      console.log(
        `%c[${moduleName}] Poll event: ${pollFor} Interval: ${intervalMs} ms, Retry: ${retryCount}, Reset Count: ${intervalResetCount}`,
        'padding:2px;background:#e07700;color:white'
      );

      let conditionAction;

      if (typeof conditionToFulfil === 'function') {
        conditionAction = pollingParams ? conditionToFulfil(pollingParams) : pollEvent.conditionToFulfil();
      } else {
        conditionAction = conditionToFulfil;
      }

      if (!conditionAction || typeof conditionAction.type !== 'string') {
        console.log(moduleName, `'Invalid action returned by conditionToFulfil' for ${pollFor}`);
        yield all([put(pollStopByResourceAction(pollFor)), put(apiFailed)]);
        return;
      }

      yield put(conditionAction);
      yield delay(intervalMs);

      retryCount += 1;
      intervalResetCount += 1;

      if (intervalResetCount > stopAfterRetries) {
        console.log(moduleName, `${pollFor} polling stopped after max retries`);
        yield all([put(pollStopByResourceAction(pollFor)), put(apiFailed)]);
        return;
      }

      yield put(pollingParams ? pollStartAction(pollFor, pollingParams) : pollStartAction(pollFor));
    } catch (error) {
      console.error(moduleName, `${pollFor} polling error: `, error);
      yield all([put(pollStopByResourceAction(pollFor)), put(apiFailed), put(getDataFailureAction(error))]);
      return;
    }
  }
}

/**
 * Poll Saga worker.
 */
function* pollingSaga() {
  console.log(moduleName, 'Inside pollingSaga');

  const { pollingEventsData, currentUserRole } = yield select(state => ({
    pollingEventsData: state?.clientPolling?.pollingEvents || {},
    currentUserRole: state?.identity?.role
  }));
  const pollingEvents = Object.keys(pollingEventsData);

  yield all(pollingEvents.map(pollFor => call(pollEventSaga, pollFor, pollingEventsData[pollFor], currentUserRole)));
}

// Wait for polling action, then fire another request
// Cancel polling once condition is met

function* watchPoll() {
  while (true) {
    console.log(moduleName, 'Inside while watchPoll');
    yield take(EVENT_POLLING_START);
    yield race([call(pollingSaga), take(EVENT_POLLING_STOP)]);
  }
}

// Daemonize tasks in parallel
export default function* watchPollingSaga() {
  console.log(moduleName, 'Beginning');
  yield* [
    watchPoll()
    // other watchers here
  ];
}
